import { useCallback, useState } from "react";
import { Phone } from "../services/api";

export type PhoneRevealKind = "ad" | "user";

interface UsePhoneRevealArgs {
  kind: PhoneRevealKind;
  id: string;
}

// Central place for the "reveal phone numbers" flow behind /phone/reveal/*.
// Handles the three server responses: 200 (numbers returned), 429 + 1093
// (captcha required — caller renders Turnstile and calls reveal again with
// the token), and 429 + 1064 (hard rate limit — terminal state).
export const usePhoneReveal = ({ kind, id }: UsePhoneRevealArgs) => {
  const [state, setState] = useState("idle");
  const [phones, setPhones] = useState([]);
  const [hardLimitMessage, setHardLimitMessage] = useState(null);

  const reset = useCallback(() => {
    setState("idle");
    setPhones([]);
    setHardLimitMessage(null);
  }, []);

  const reveal = useCallback(
    async (captchaToken?: string) => {
      if (!id) return;
      setState("loading");
      try {
        const call = kind === "ad" ? Phone.revealAd : Phone.revealUser;
        const result = await call(id, captchaToken);
        if (result.kind === "success") {
          setPhones(result.phones);
          setState("revealed");
          return;
        }
        if (result.kind === "captchaRequired") {
          setState("captchaRequired");
          return;
        }
        if (result.kind === "rateLimit") {
          setHardLimitMessage(result.message || null);
          setState("hardLimit");
          return;
        }
      } catch (e) {
        // Standard API error handling (auth, 5xx, network) already surfaced
        // a toast via handleError inside the reveal helper. Swallow here so
        // the caller can just react to `state === "error"`.
        setState("error");
      }
    },
    [id, kind]
  );

  return { state, phones, hardLimitMessage, reveal, reset };
};



// WEBPACK FOOTER //
// ./src/hooks/usePhoneReveal.ts